import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { BaseComponent } from '../../../core/base.component';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HeaderService } from '../../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { NotificationService } from '../../../notification/notification.service';
import { AuthService } from '../../../core/security/auth.service';
import { RolesEnum } from '../../../core/security/roles.enum';
import { CurrentUser, PbxGroupModel } from '../../../api/models';
import { MatDialog } from '@angular/material/dialog';
import { LocalPbxService } from '../../local-pbx.service';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatTextInputComponent } from '../../../editors/mat-text-input/mat-text-input.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import moment from 'moment/moment';
import { DateUtils } from '../../../core/utilities/date-time-helpers';
import { FlexModule } from '@angular/flex-layout';
import { MatTimePickerComponent } from '../../../editors/mat-time-picker/mat-time-picker.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgIf } from '@angular/common';

@Component({
  selector: 'app-edit-pbx-group',
  templateUrl: './edit-pbx-group.component.html',
  styleUrls: ['./edit-pbx-group.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatCard,
    MatCardTitle,
    MatCardContent,
    MatTextInputComponent,
    MatTooltip,
    MatButton,
    Angular2PromiseButtonModule,
    FlexModule,
    MatTimePickerComponent,
    MatProgressSpinner,
    NgIf,
  ],
})
export class EditPbxGroupComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  form: UntypedFormGroup;
  submitted: boolean;
  serverError: boolean;
  editModel: PbxGroupModel = {
    id: null,
    accountType: 'acds',
    display: '',
    allAgents: '',
    name: '',
    workHoursStart: 0,
    workHoursEnd: 1440, // 24 hours in minutes
  };

  private paramsSubscription;
  private id: string;

  buttonSubmitPromise: Promise<void>;
  isLoading: boolean = false;

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    authService: AuthService,
    public dialog: MatDialog,
    public pbxService: LocalPbxService,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
  ) {
    super(route, headerService, titleService, authService);

    this.paramsSubscription = this.route.params.subscribe(params => {
      this.id = params['id'];
      console.log(this.id);
    });

    this.headerService.showBackButton(true);
    this.createForm();
  }

  onCurrentUserChanged(user: CurrentUser) {
    super.onCurrentUserChanged(user);

    if (user) {
      this.hasEditAccess = this.authService.hasAccess([RolesEnum.PbxGroups_Edit, RolesEnum.PbxGroups_Delete]);
    } else {
      this.hasEditAccess = false;
    }
  }

  createForm(): void {
    const controlsConfig: any = {
      account: ['', []],
      display: ['', []],
      allAgents: ['', []],
      workHoursStart: [0, []],
      workHoursEnd: [1440, []], // 24 hours in minutes
    };

    this.form = this.fb.group(controlsConfig, {
      //validators: matchFields('mbPin', 'mbPinConfirm')
    });
  }

  async onSubmit() {
    this.serverError = false;

    if (this.form.valid && this.hasEditAccess) {
      this.buttonSubmitPromise = this.processSubmit();
    } else {
      this.notificationService.notifyDanger('Form is not valid');
    }
  }

  async processSubmit(): Promise<void> {
    try {
      this.submitted = true;

      const reqData = this.prepareFormModel();
      this.editModel = await this.pbxService.savePbxGroup(reqData);
      this.notificationService.notifySuccess('Extension updated successfully');
      this.ngOnChanges();
    } catch (e) {
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): PbxGroupModel {
    const formModel = this.form.value;
    const workHoursStart: Date = formModel.workHoursStart?.toDate();
    const workHoursEnd: Date = formModel.workHoursEnd?.toDate();

    const resultModel: PbxGroupModel = {
      id: this.editModel.id,
      accountType: this.editModel.accountType,
      name: this.editModel.name,
      allAgents: this.editModel.allAgents,
      display: this.editModel.display,
      workHoursStart: workHoursStart ? DateUtils.dateTimeToMinutes(workHoursStart) : undefined,
      workHoursEnd: workHoursEnd ? DateUtils.dateTimeToMinutes(workHoursEnd) : undefined,
    };

    console.log('prepareFormModel', resultModel);

    return resultModel;
  }

  ngOnInit() {
    super.ngOnInit();

    this.isLoading = true;
    this.loadPbxGroupById()
      .then(() => {
        this.createForm();
        this.ngOnChanges();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  ngOnChanges(changes?: SimpleChanges): void {
    const defValues: any = {
      account: this.editModel.name,
      display: this.editModel.display,
      allAgents: this.editModel.allAgents,
      workHoursStart:
        this.editModel.workHoursStart >= 0
          ? moment(DateUtils.minutesToDateTime(this.editModel.workHoursStart))
          : undefined,
      workHoursEnd:
        this.editModel.workHoursEnd >= 0 ? moment(DateUtils.minutesToDateTime(this.editModel.workHoursEnd)) : undefined,
    };

    this.form.reset(defValues);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.paramsSubscription?.unsubscribe();
    this.headerService.showBackButton(false);
  }

  private async loadPbxGroupById(): Promise<void> {
    this.editModel = await this.pbxService.getPbxGroupById(this.id);
  }
}
